import { Link } from "react-router-dom";
import { Shield, AlertCircle, Phone, Camera, Lock, Users, MessageSquare, Globe } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from '../components/SEO';

const SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  'headline': 'Cybermobbing melden: Hilfe für Kinder, Schüler und Eltern',
  'description': 'Was ist Cybermobbing? Wie erkenne ich es, was kann ich tun und wo melde ich es? Kostenlose Hilfe und anonyme Meldestelle für Betroffene.',
  'author': { '@type': 'Organization', 'name': 'mobbing-melden.de' },
  'publisher': { '@type': 'Organization', 'name': 'mobbing-melden.de', 'url': 'https://www.mobbing-melden.de' },
  'inLanguage': 'de-DE',
};

const FORMS = [
  { icon: MessageSquare, color: "#FFC4D6", title: "Beleidigungen & Bedrohungen", desc: "Hasskommentare, Beschimpfungen oder Drohungen per WhatsApp, Instagram, TikTok oder anderen Kanälen." },
  { icon: Globe, color: "#BDE0FE", title: "Öffentliche Bloßstellung", desc: "Peinliche Fotos oder Videos werden ohne Erlaubnis veröffentlicht, geteilt oder kommentiert." },
  { icon: Lock, color: "#F6E05E", title: "Account-Hacking", desc: "Jemand übernimmt deinen Account und schreibt in deinem Namen oder sperrt dich aus." },
  { icon: Users, color: "#A0E8AF", title: "Ausgrenzung in Gruppen", desc: "Du wirst aus Gruppen-Chats gekickt, blockiert oder von Online-Aktivitäten ausgeschlossen." },
  { icon: Camera, color: "#FFC4D6", title: "Bildmissbrauch (Sharenting)", desc: "Private Bilder werden ohne Erlaubnis verbreitet — auch intime Aufnahmen (strafbar!)." },
  { icon: AlertCircle, color: "#BDE0FE", title: "Fake-Profile", desc: "Jemand erstellt ein gefälstes Profil in deinem Namen und schadet damit deinem Ruf." },
];

const STEPS = [
  { number: 1, title: "Ruhig bleiben & nicht antworten", text: "Reagiere nicht auf Provokationen — das verstärkt das Cybermobbing oft. Blockiere die Person zuerst." },
  { number: 2, title: "Beweise sichern", text: "Screenshots von allen Nachrichten, Kommentaren und Profilen machen — mit Datum und Uhrzeit." },
  { number: 3, title: "Melden auf der Plattform", text: "Nutze die Melde-Funktion der jeweiligen App (Instagram, TikTok, WhatsApp). Beleidigungen können gelöscht werden." },
  { number: 4, title: "Vertrauensperson einweihen", text: "Erzähl einem Elternteil, Lehrer oder einer anderen Vertrauensperson. Du musst das nicht alleine tragen." },
  { number: 5, title: "Anonym melden", text: "Reiche eine Meldung bei mobbing-melden.de ein — anonym, kostenlos, ohne Anmeldung." },
  { number: 6, title: "Bei Straftaten: Polizei", text: "Drohungen, Bildmissbrauch und Stalking sind Straftaten. Zeig es an — mit deinen Screenshots als Beweise." },
];

const PLATFORMS = [
  { name: "Instagram", action: "Profil/Beitrag → ⋯ → Melden" },
  { name: "TikTok", action: "Video/Kommentar → Teilen → Melden" },
  { name: "WhatsApp", action: "Nachricht gedrückt halten → Melden" },
  { name: "Snapchat", action: "Profil → ⋮ → Melden" },
  { name: "YouTube", action: "Kommentar → ⋮ → Melden" },
  { name: "Facebook", action: "Beitrag → ⋯ → Beitrag melden" },
];

const HOTLINES = [
  { name: "Nummer gegen Kummer", number: "116 111", desc: "Kostenlos, anonym, Mo–Sa 14–20 Uhr" },
  { name: "Cybermobbing-Hilfswerk", number: "cybermobbing-hilfswerk.de", desc: "Online-Beratung & Notfall-Hotline" },
  { name: "Klicksafe Helpline", number: "klicksafe.de", desc: "Medienkompetenzzentrum der EU" },
];

export default function Cybermobbing() {
  return (
    <div className="min-h-screen bg-[#FFFDF9]">
      <Header />
      <SEO
        title="Cybermobbing melden: Hilfe für Schüler, Kinder & Eltern"
        description="Was tun bei Cybermobbing? Beweise sichern, auf Plattformen melden, anonym Hilfe holen. Kostenlose Anlaufstelle für Betroffene in Deutschland."
        path="/cybermobbing"
        schema={SCHEMA}
      />

      <main className="px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-4xl mx-auto">

          {/* Hero */}
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-[#FFC4D6]/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <Shield className="w-10 h-10 text-[#C53030]" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#2D3748] mb-4" style={{ fontFamily: 'Nunito' }}>
              Cybermobbing: Was tun, wo melden, wie helfen?
            </h1>
            <p className="text-lg text-[#718096] max-w-2xl mx-auto">
              Cybermobbing passiert 24/7 — auch wenn du zu Hause bist. Hier erfährst du, was du sofort tun kannst, wo du Beweise sicherst und wer dir kostenlos hilft.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/melden" className="inline-flex items-center justify-center gap-2 bg-[#FFC4D6] text-[#C53030] font-bold px-8 py-4 rounded-full hover:bg-[#FFB3C1] transition-all">
                Jetzt anonym melden
              </Link>
              <a href="#was-tun" className="inline-flex items-center justify-center gap-2 border-2 border-[#FFC4D6] text-[#C53030] font-bold px-8 py-4 rounded-full hover:bg-[#FFC4D6]/20 transition-all">
                Was tun? →
              </a>
            </div>
          </div>

          {/* Definition */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-10">
            <h2 className="text-xl font-bold text-[#2D3748] mb-3" style={{ fontFamily: 'Nunito' }}>Was ist Cybermobbing?</h2>
            <p className="text-[#718096] mb-4">
              Cybermobbing bedeutet, dass jemand über das Internet, Social Media, Messenger oder Spiele absichtlich und wiederholt beleidigt, bedroht, bloßgestellt oder ausgeschlossen wird. Im Unterschied zu klassischem Mobbing hört es nie auf — auch nicht nach der Schule oder der Arbeit.
            </p>
            <div className="flex flex-wrap gap-2">
              {["Wiederholend", "Absichtlich", "24/7 erreichbar", "Anonyme Täter möglich", "Öffentlich sichtbar", "Strafbar"].map((tag, i) => (
                <span key={i} className="bg-[#FFC4D6]/30 text-[#C53030] text-xs font-semibold px-3 py-1 rounded-full">{tag}</span>
              ))}
            </div>
          </div>

          {/* Forms */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-6" style={{ fontFamily: 'Nunito' }}>
              Formen von Cybermobbing
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {FORMS.map((form, i) => {
                const Icon = form.icon;
                return (
                  <div key={i} className="bg-white rounded-xl border border-gray-100 p-5 flex gap-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${form.color}30` }}>
                      <Icon className="w-5 h-5" style={{ color: form.color }} />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#2D3748] text-sm mb-1">{form.title}</h3>
                      <p className="text-[#718096] text-sm">{form.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Steps */}
          <div className="mb-12" id="was-tun">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-6" style={{ fontFamily: 'Nunito' }}>
              Was tun? — 6 Schritte sofort
            </h2>
            <div className="space-y-4">
              {STEPS.map((step, i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-100 p-5 flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#FFC4D6]/30 flex items-center justify-center flex-shrink-0 font-bold text-[#C53030]">
                    {step.number}
                  </div>
                  <div>
                    <h3 className="font-bold text-[#2D3748] mb-1">{step.title}</h3>
                    <p className="text-[#718096] text-sm">{step.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Platform reporting */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-6" style={{ fontFamily: 'Nunito' }}>
              So meldest du auf den Plattformen
            </h2>
            <div className="grid md:grid-cols-2 gap-3">
              {PLATFORMS.map((p, i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-100 px-5 py-4 flex justify-between items-center">
                  <span className="font-bold text-[#2D3748] text-sm">{p.name}</span>
                  <span className="text-[#718096] text-xs text-right">{p.action}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Hotlines */}
          <div className="bg-[#FFF5F7] rounded-2xl p-6 mb-10">
            <h2 className="text-xl font-bold text-[#2D3748] mb-4 flex items-center gap-2" style={{ fontFamily: 'Nunito' }}>
              <Phone className="w-5 h-5 text-[#C53030]" />
              Hotlines & Hilfsangebote
            </h2>
            <div className="space-y-3">
              {HOTLINES.map((h, i) => (
                <div key={i} className="bg-white rounded-xl p-4 flex justify-between items-center gap-4">
                  <div>
                    <div className="font-bold text-[#2D3748] text-sm">{h.name}</div>
                    <div className="text-[#A0AEC0] text-xs">{h.desc}</div>
                  </div>
                  <div className="font-bold text-[#C53030] text-sm text-right">{h.number}</div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-br from-[#FFF5F7] to-[#EBF8FF] rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-3" style={{ fontFamily: 'Nunito' }}>
              Jetzt anonym Cybermobbing melden
            </h2>
            <p className="text-[#718096] mb-6 max-w-xl mx-auto">
              Du musst deinen Namen nicht angeben. Lade Screenshots hoch, beschreibe was passiert — wir helfen dir beim nächsten Schritt.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/melden" className="inline-flex items-center justify-center gap-2 bg-[#FFC4D6] text-[#C53030] font-bold px-8 py-4 rounded-full hover:bg-[#FFB3C1] transition-all">
                Anonym melden
              </Link>
              <Link to="/eltern" className="inline-flex items-center justify-center gap-2 border-2 border-[#BDE0FE] text-[#1A365D] font-bold px-8 py-4 rounded-full hover:bg-[#BDE0FE]/20 transition-all">
                Ratgeber für Eltern →
              </Link>
            </div>
          </div>

        </div>
      </main>
      <Footer />
    </div>
  );
}
